/**
 * DayView — single-day hour strip (11E.7).
 *
 * The Day view is where an operator resolves the overflow the Month view
 * hides, so it never truncates: every session for the day renders in full
 * density with its roster preview, and the hour rows keep the same
 * `CALENDAR_UI.TIME_SLOTS` bounds as the Week grid so the two views agree
 * on what "the working day" means.
 *
 * Drag-to-reschedule is intentionally supported here too — same drop
 * contract as the week grid (`text/session-id`), so the handler in
 * CalendarPage serves both.
 */

import React from 'react';

import { SessionChip } from '@features/calendar/components/SessionChip';
import { CALENDAR_UI } from '@features/calendar/config/ui';
import { fmtIsoDate } from '@features/calendar/utils/tz';
import type { CalendarBlockResponse } from '@features/calendar/api/blocks';
import type { SessionResponse } from '@features/calendar/api/sessions';

export interface DayViewProps {
  /** The day being shown, as a tenant-tz wall-clock Date. */
  date: Date;
  tenantTz: string;
  /** `${iso}_${hour}` → sessions starting in that hour. */
  sessionsByDayHour: Map<string, SessionResponse[]>;
  blocks: CalendarBlockResponse[];
  subjectNames?: Record<string, string>;
  studentNames?: Record<string, string>;
  onSessionClick: (session: SessionResponse) => void;
  onDropSession: (sessionId: string, date: Date, hour: number) => void;
}

export const DayView: React.FC<DayViewProps> = ({
  date,
  tenantTz,
  sessionsByDayHour,
  blocks,
  subjectNames,
  studentNames,
  onSessionClick,
  onDropSession,
}) => {
  const iso = fmtIsoDate(date);

  return (
    <div data-testid="candeza-calendar-day-grid">
      {blocks.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
          {blocks.map((b) => (
            <div
              key={b.id}
              data-testid={`candeza-calendar-block-${b.id}`}
              data-block-kind={b.kind}
              style={{
                fontSize: 12,
                padding: '6px 10px',
                borderRadius: 6,
                background: b.kind === 'holiday' ? '#fef9c3' : '#f1f5f9',
                color: b.kind === 'holiday' ? '#854d0e' : '#475569',
                border: `1px solid ${b.kind === 'holiday' ? '#fde047' : '#e2e8f0'}`,
              }}
            >
              <strong>{b.kind === 'holiday' ? 'Holiday' : 'Blocked'}:</strong> {b.title}
            </div>
          ))}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '72px 1fr',
          gap: 1,
          background: '#e2e8f0',
          border: '1px solid #e2e8f0',
          borderRadius: 8,
          overflow: 'hidden',
        }}
      >
        {CALENDAR_UI.TIME_SLOTS.map((time) => {
          const hour = parseInt(time.split(':')[0], 10);
          const cellSessions = sessionsByDayHour.get(`${iso}_${hour}`) ?? [];
          return (
            <React.Fragment key={time}>
              <div
                style={{
                  background: '#f8fafc',
                  padding: '8px 6px',
                  fontSize: 11,
                  color: '#64748b',
                  textAlign: 'right',
                }}
              >
                {time}
              </div>
              <div
                data-testid={`candeza-calendar-day-cell-${iso}-${hour}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const sessionId = e.dataTransfer.getData('text/session-id');
                  if (sessionId) onDropSession(sessionId, date, hour);
                }}
                style={{
                  background: '#ffffff',
                  minHeight: 44,
                  padding: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                }}
              >
                {cellSessions.map((s) => (
                  <SessionChip
                    key={s.id}
                    session={s}
                    tenantTz={tenantTz}
                    subjectNames={subjectNames}
                    studentNames={studentNames}
                    density="full"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSessionClick(s);
                    }}
                  />
                ))}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};
